import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useOtPurposesByStep } from "@jti/rest-api";
import { LegalText } from "@jti/ui";
import { useFormContext } from "@jti/ui/components/Form";
import { Recaptchabar } from "@jti/ui/components/Recaptchabar";
import { RegistrationPolicyCheckFields } from "@jti/ui/components/RegistrationPolicyCheckFields";
import { TranslationHtmlWrapper } from "@jti/ui/components/TranslationHtmlWrapper";
import { ValidationMethods } from "@jti/ui/components/ValidationMethods";
import { useConfig } from "@jti/ui/providers/useConfig";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { ConsentsLegalIntroduction } from "../../../../components/ConsentsLegalIntroduction";
import { useAemModel } from "../../../../contexts/AemModelContext";
import { useAemLanguagesModel } from "../../../../contexts/LanguagesContext";
import { useSessionAuthData } from "../../../../hooks/useSessionAuthData/useSessionAuthData";
import { AGE_VERIFICATION_STEP } from "../constants";
import { prepareFieldsForAV } from "../utils/prepareFieldsForAv/prepareFieldsForAv";
import { transformToCasUser } from "../utils/transformToCasStructure/transformToCasStructure";
import styles from "./AgeVerification.module.scss";
const prepareRedirectUrl = () => {
    const parsedUrl = URL.parse(window.location.href);
    if (!parsedUrl) {
        return window.location.href;
    }
    const preparedRedirectUrl = `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}/${AGE_VERIFICATION_STEP}`;
    return preparedRedirectUrl;
};
const getCurrentLanguage = (languages) => {
    var _a;
    return (_a = languages.find(({ current }) => current)) === null || _a === void 0 ? void 0 : _a.associatedCookieValue;
};
const AgeVerificationPage = ({ onNext, fieldName = (name) => name, isLast, isOptional, isSubmitting = false, showDeviceRegistrationStep = false, isDeviceCodeFromUrlExists, }) => {
    var _a, _b, _c, _d, _e;
    const config = useConfig();
    const { __ } = useTranslations();
    const [verificationMethodCode, setVerificationMethodCode] = useState("");
    const [verificationMethodImage, setVerificationMethodImage] = useState("");
    const languages = useAemLanguagesModel();
    const { setValue, getValues, reset } = useFormContext();
    const { saveRegisterData, getRegisterData, clearRegisterData } = useSessionAuthData();
    const aemModel = useAemModel();
    const { data: purposes, isError: isOtError, isFetching: isFetchingOt } = useOtPurposesByStep("registration", isLast);
    const additionalDataForVerification = useMemo(() => {
        const requestObjectCasObject = transformToCasUser(getValues(), getCurrentLanguage(languages));
        return prepareFieldsForAV(requestObjectCasObject, getValues());
    }, [getValues, languages]);
    const onLoadDataFromCache = () => {
        const registerData = getRegisterData();
        // isAlreadyFormFilled() is used for edge case when user will use back button from veriff page
        // and sessionstorage will not be cleared
        if (registerData && !isAlreadyFormFilled()) {
            reset(registerData);
            const methodCodeFromRegisterSessionStorage = (registerData === null || registerData === void 0 ? void 0 : registerData.ageVerificationMethodCode) || "";
            setVerificationMethodCode(methodCodeFromRegisterSessionStorage);
        }
        clearRegisterData();
    };
    const onSaveDataToCache = () => {
        saveRegisterData(getValues());
    };
    const isAlreadyFormFilled = () => {
        return Object.values(getValues(["firstname", "lastname", "date_of_birth", "email"])).every(Boolean);
    };
    const onSuccessAgeVerification = (ageVerificationUuid) => {
        setValue(fieldName("age_verification_uuid"), ageVerificationUuid);
        onNext();
    };
    const renderOptionalLabel = () => {
        if (isOptional) {
            return __("auth.registration.age_verification.title_step_optional");
        }
    };
    const onSelectMethod = (method) => {
        setVerificationMethodCode(method);
        setVerificationMethodImage(aemModel.ageVerificationMethodImages[method]);
    };
    const onFail = () => {
        if (isOptional) {
            onNext();
        }
    };
    const isPending = isSubmitting || (isLast && isFetchingOt);
    const submitButtonLabel = useMemo(() => {
        if (showDeviceRegistrationStep) {
            return __("auth.age_verification.next_step.device_registration");
        }
        if (isDeviceCodeFromUrlExists) {
            return __("auth.age_verification.btn_create_and_device_register");
        }
        return __("auth.age_verification.btn_create");
    }, [showDeviceRegistrationStep, isDeviceCodeFromUrlExists]);
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.headerWrapper, children: [_jsxs("h2", { className: styles.header, children: [AGE_VERIFICATION_STEP, ". ", __("auth.age_verification.title"), " ", renderOptionalLabel()] }), _jsx(TranslationHtmlWrapper, { className: styles.description, text: "auth.age_verification.info_description", htmlData: [config.PRIVACY_PAGE_URL] })] }), _jsxs(ValidationMethods, { buttonClassName: styles.avSubmitButton, isOptional: isOptional, selectedMethod: verificationMethodCode, isPending: isSubmitting, isDisabled: isOtError, selectedMethodImage: verificationMethodImage, selectMethod: onSelectMethod, onSuccess: onSuccessAgeVerification, onFail: onFail, buttonLabel: submitButtonLabel, userData: {
                    email: getValues("email"),
                    firstname: getValues("firstname"),
                    lastname: getValues("lastname"),
                    dateOfBirth: getValues("date_of_birth"),
                    additionalData: additionalDataForVerification,
                }, onLoadDataFromCache: onLoadDataFromCache, onSaveDataToCache: onSaveDataToCache, baseUrl: prepareRedirectUrl(), children: [isLast && _jsx(ConsentsLegalIntroduction, { legalText: config.consentsLegalText }), isLast && (_jsx(RegistrationPolicyCheckFields, { policyFields: purposes, fieldName: (name) => `purposes.${name}`, disabled: isPending, isError: isOtError, oneTrustErrorLinkText: (_a = aemModel.consents) === null || _a === void 0 ? void 0 : _a.oneTrustErrorLinkText, oneTrustErrorMessage: (_b = aemModel.consents) === null || _b === void 0 ? void 0 : _b.oneTrustErrorMessage, oneTrustErrorRedirectUrl: (_c = aemModel.consents) === null || _c === void 0 ? void 0 : _c.oneTrustErrorRedirectUrl, isOldStyle: false, consentOptionsListType: (_d = aemModel.consents) === null || _d === void 0 ? void 0 : _d.consentOptionsListType })), _jsx("span", { className: styles.required, children: __("profile.password.required_field") }), isLast && _jsx(LegalText, { legalText: (_e = aemModel.consents) === null || _e === void 0 ? void 0 : _e.legalConditionsForConsents, className: styles.legalText }), config.reCaptchaEnabled && config.brandingInformationEnabled && _jsx(Recaptchabar, { isPaddingRemoved: true })] })] }));
};
export default AgeVerificationPage;
